import {defineMessages, FormattedMessage} from 'react-intl';
import {useEffect, useRef, useState} from 'react';

import {chevronDown, chevronUp} from '@sail/icons/react/Icon';
import {Icon, Link, view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {TopBar} from '@stripe-internal/conversations-ui';

import {CancelButton} from './CancelButton';
import {Details} from './Details';
import {RACError} from './RACError';
import {useRACDetails} from '../../hooks/useRACDetails';

const messages = defineMessages({
  title: {
    id: 'conversations_assistant.requestACallTopBar.title',
    description: 'Title of the request a call top bar',
    defaultMessage: 'Have us call you',
  },
  showDetails: {
    id: 'conversations_assistant.requestACallTopBar.showDetails',
    description: 'Toggle button to show more details about call state',
    defaultMessage: 'Show details',
  },
  hideDetails: {
    id: 'conversations_assistant.requestACallTopBar.hideDetails',
    description: 'Toggle button to hide details about call state',
    defaultMessage: 'Hide details',
  },
});

export function RACTopBar({conversation}: {conversation: Conversation}) {
  const {
    userName,
    userPhoneNumber,
    error,
    loading,
    verificationCodes,
    racStatus,
    isCancelLoading,
    cancelRac,
    callEnded,
  } = useRACDetails(conversation);

  const leaveRef = useRef<boolean>(false);

  const [showDetails, setShowDetails] = useState<boolean>(true);

  useEffect(() => {
    if (leaveRef.current) {
      return;
    }

    if (callEnded && conversation) {
      leaveRef.current = true;
      conversation.leave();
    }
  }, [conversation, callEnded]);

  if (error) {
    return <RACError serverError={error} />;
  }

  return (
    <TopBar data-testId="widget-request-a-call">
      <view.div css={{stack: 'y'}}>
        <view.div
          css={{
            font: 'label.medium.emphasized',
            stack: 'x',
            distribute: 'space-between',
          }}
        >
          <FormattedMessage {...messages.title} />
          <view.div>
            <Link
              onPress={() => setShowDetails(!showDetails)}
              css={{
                font: 'label.small.emphasized',
              }}
            >
              <FormattedMessage
                {...(showDetails ? messages.hideDetails : messages.showDetails)}
              />
              <Icon
                icon={showDetails ? chevronUp : chevronDown}
                size="xxsmall"
                css={{marginLeft: 'xsmall'}}
              />
            </Link>
            <CancelButton
              isLoading={isCancelLoading}
              racStatus={racStatus}
              onCancel={() => cancelRac()}
            />
          </view.div>
        </view.div>
        {showDetails ? (
          <Details
            userName={userName}
            userPhoneNumber={userPhoneNumber}
            userCode={verificationCodes?.userCode || ''}
            agentCode={verificationCodes?.agentCode || ''}
            error={error}
            loading={loading}
          />
        ) : null}
      </view.div>
    </TopBar>
  );
}
